import "@/App.css";
import { Component, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/i18n/translations";
import { SmoothScrollProvider } from "@/hooks/useSmoothScroll";
import { Toaster } from "@/components/ui/sonner";
import Home from "@/pages/Home";
import CaseStudyPage from "@/pages/CaseStudyPage";

// Route-level code splitting: admin, sandbox and legal pages are not part of
// the public visitor journey and stay out of the main bundle.
const Impressum = lazy(() => import("@/pages/Impressum"));
const Datenschutz = lazy(() => import("@/pages/Datenschutz"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const MotionLab = lazy(() => import("@/pages/MotionLab"));

const RouteFallback = () => (
  <div className="min-h-screen bg-[#05070d]" aria-hidden="true" />
);

// Letzte Verteidigungslinie: Faengt unerwartete Render-/Commit-Fehler ab,
// damit nie wieder eine komplett schwarze, tote Seite entsteht.
class AppErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("AppErrorBoundary caught:", error, info?.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          data-testid="app-error-fallback"
          className="min-h-screen bg-[#05070d] text-white flex items-center justify-center px-6"
        >
          <div className="max-w-md text-center">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#00d4ff]">CloudService</p>
            <h1 className="mt-4 text-2xl font-semibold">Etwas ist schiefgelaufen.</h1>
            <p className="mt-3 text-sm text-white/60 leading-relaxed">
              Ein unerwarteter Fehler ist aufgetreten. Bitte lade die Seite neu — deine Daten sind nicht verloren.
            </p>
            <button
              data-testid="app-error-reload-button"
              onClick={() => window.location.reload()}
              className="mt-6 inline-flex items-center rounded-full bg-[#00d4ff] px-6 py-2.5 text-sm font-medium text-[#05070d] hover:bg-[#33ddff] transition-colors duration-200"
            >
              Seite neu laden
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <div className="App cs-noise">
      <AppErrorBoundary>
        <LanguageProvider>
          <BrowserRouter>
            <SmoothScrollProvider>
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/references/:projectId" element={<CaseStudyPage />} />
                  <Route path="/impressum" element={<Impressum />} />
                  <Route path="/datenschutz" element={<Datenschutz />} />
                  <Route path="/admin" element={<AdminPage />} />
                  <Route path="/motion-lab" element={<MotionLab />} />
                </Routes>
              </Suspense>
              <Toaster position="bottom-right" theme="dark" />
            </SmoothScrollProvider>
          </BrowserRouter>
        </LanguageProvider>
      </AppErrorBoundary>
    </div>
  );
}

export default App;
